import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, TextField } from '@ofself/shell';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import DataTable from '../../components/nucleus/DataTable';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { decryptField, isEncrypted } from '../../utils/fieldEncryption';
import { schemaLabel, expandSchemaIds } from '../../utils/schemaUtils';
import { FormColumn, FormBand, typedConfirm } from './selfForm';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { useSpaceAccess } from '../../nucleus/access';
import NoAccess from '../../components/nucleus/NoAccess';
import { Trash2 } from 'lucide-react';
import { dateOf, plural } from '../../nucleus/format';

// DATA — what you hold, in bulk: entries still encrypted under a field their
// schema no longer encrypts, and moving many nodes to the trash at once. They
// wait in Graph → Trash, restorable, for as long as the server keeps trash
// (keptWords: 30 days when its auto-purge is on, else until deleted for good).
export default function DataPage() {
    const { section, page, groupId, to, navigate } = usePlace();
    const { noAccess } = useSpaceAccess();
    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            verbs={<Verb icon={<Trash2 />} onClick={() => navigate(to('graph', 'trash'))}>Open the trash</Verb>}
        >
            <FormColumn wide>
                {!groupId && <EncryptionBand />}
                {/* a member with No access holds none of the group's nodes */}
                {noAccess ? <NoAccess /> : <BulkDelete key={groupId || 'personal'} />}
            </FormColumn>
        </NucleusPage>
    );
}

// ── encryption, undone ────────────────────────────────────────────────────
// Leaving these encrypted is a fine permanent state; the one reason to
// convert is that encrypted entries can't survive the loss of your key. Decrypted in this browser with
// the key your password derives; the server can't. Personal nodes only.
function EncryptionBand() {
    const toast = useToast();
    const [summary, setSummary] = useState(null);
    const [asking, setAsking] = useState(null);
    const [running, setRunning] = useState(null);
    const [report, setReport] = useState(null);
    const load = useCallback(() => api.request('/nodes/encryption-legacy')
        .then((r) => setSummary(r?.schemas || []))
        .catch(() => { setSummary([]); toast.error('Could not check for encrypted entries'); }), [toast]);
    useEffect(() => { load(); }, [load]);
    if (summary === null) return null;

    return (
        <FormBand
            title="Encrypted entries"
            aside={summary.length ? plural(summary.reduce((n, r) => n + r.node_count, 0), 'entry', 'entries') : 'none'}
            lead="Entries written while their schema encrypted a field stay encrypted after it stops: only you can read them, and only you can change that."
        >
            {summary.length === 0 && <Note>Nothing to convert. None of your entries hold encrypted data for a field that has since been un-encrypted.</Note>}
            {summary.map((row) => (
                <Row
                    key={row.schema_name}
                    name={row.schema_name}
                    sub={running?.schema_name === row.schema_name ? `Converting ${running.done} of ${running.total}…` : `Encrypted: ${row.fields.join(', ')}`}
                    end={plural(row.node_count, 'entry', 'entries')}
                    onPress={running ? undefined : () => setAsking(row)}
                />
            ))}
            {summary.length > 0 && <Note>Press a kind to decrypt it permanently.</Note>}
            {report && (
                <Note tone={report.failures.length ? 'warn' : undefined}>
                    {report.converted} converted to plain text.
                    {report.failures.length > 0 && ` ${report.failures.length} could not be and are still encrypted: ${report.failures.slice(0, 5).map((f) => `${f.title} (${f.reason})`).join('; ')}${report.failures.length > 5 ? '…' : ''}`}
                </Note>
            )}
            <DecryptSheet
                row={asking}
                onClose={() => setAsking(null)}
                onProgress={setRunning}
                onDone={(r) => { setAsking(null); setRunning(null); setReport(r); load(); }}
            />
        </FormBand>
    );
}

function DecryptSheet({ row, onClose, onProgress, onDone }) {
    const { encKeys, unlockEncKeys } = useAuth();
    const toast = useToast();
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => { setPassword(''); }, [row]);

    const convert = async (e) => {
        e.preventDefault();
        let keys = encKeys;
        if (!keys?.privateKey) {
            keys = await unlockEncKeys(password);
            if (!keys?.privateKey) { toast.error('Could not unlock your key. Check your password.'); return; }
        }
        setBusy(true);
        const failures = [];
        let converted = 0;
        onProgress({ schema_name: row.schema_name, done: 0, total: row.node_count });
        try {
            // Page through: each conversion removes a node from the server's set.
            for (let guard = 0; guard < 50; guard++) {
                const batch = await api.request(`/nodes/encryption-legacy?schema_name=${encodeURIComponent(row.schema_name)}&limit=100`);
                const nodes = batch.nodes || [];
                if (!nodes.length) break;
                for (const node of nodes) {
                    const vj = node.value_json || {};
                    const plain = { ...vj };
                    let touched = false;
                    let failed = null;
                    for (const field of (batch.fields || [])) {
                        if (!isEncrypted(vj[field])) continue;
                        try { plain[field] = await decryptField(vj[field], field, keys); touched = true; } catch { failed = `could not decrypt “${field}”`; }
                    }
                    if (failed) failures.push({ title: node.title, reason: failed });
                    else if (touched) {
                        try { await api.updateNode(node.id, { value_json: plain }); converted += 1; } catch (err) { failures.push({ title: node.title, reason: err?.message || 'save failed' }); }
                    }
                    onProgress({ schema_name: row.schema_name, done: converted + failures.length, total: row.node_count });
                }
                // everything in this page failed: another pass would loop forever
                if (converted === 0 && failures.length >= nodes.length) break;
            }
        } catch (err) {
            toast.error(`Conversion stopped: ${err?.message || 'unknown error'}`);
        }
        setBusy(false);
        if (converted && !failures.length) toast.success(`Converted ${plural(converted, 'entry', 'entries')} to plain text`);
        onDone({ converted, failures });
    };

    return (
        <Modal
            open={!!row}
            onClose={busy ? undefined : onClose}
            title={row ? `Decrypt ${row.schema_name} permanently?` : ''}
            lead="This can’t be undone."
            size="md"
        >
            {row && (
                <form className="self-modal-form" onSubmit={convert}>
                    <Note>
                        Converting rewrites {plural(row.node_count, 'entry', 'entries')} as plain text. After that the server can read them, search can
                        index them, apps you have granted can read them, and the plain text stays in each entry’s history.
                    </Note>
                    <Note>The reason to do it anyway: encrypted entries are lost forever if you lose your key. Leaving them encrypted is fine.</Note>
                    {!encKeys?.privateKey && (
                        <TextField id="self-decrypt-password" type="password" autoComplete="current-password" label="Your password" value={password} autoFocus
                            hint="Unlocks your key in this browser. It is never sent to the server." onChange={(e) => setPassword(e.target.value)} />
                    )}
                    <SheetFoot>
                        <FootButton onClick={onClose} disabled={busy}>Keep encrypted</FootButton>
                        <FootButton danger type="submit" busy={busy} disabled={!encKeys?.privateKey && !password}>
                            Convert {row.node_count} to plain text
                        </FootButton>
                    </SheetFoot>
                </form>
            )}
        </Modal>
    );
}

// ── bulk delete (to the trash) ────────────────────────────────────────────
// THE FILTER AND THE DELETE SEE THE SAME SET: the server filters and counts;
// options come from the facets over the whole graph; "Move matching to trash"
// resolves the ids from the server page by page, so it can neither reach past
// the filter nor stop at the page cap.
const LOAD_LIMIT = 500;
const ID_PAGE = 1000;
const DELETE_CHUNK = 200;
const SELF_SOURCE = '__none__';
const LIST_FIELDS = 'id,title,schema_id,created_at,created_by_third_party_id,created_by_app_name,created_by_kind';
const RESTORE_AT_ONCE = 8;

// Some titles, quoted, then how many more: “a”, “b”, “c” and 4 more.
function named(ids, titleOf, max = 3) {
    const known = ids.map(titleOf).filter(Boolean);
    const shown = known.slice(0, max).map((t) => `“${t}”`);
    const rest = ids.length - shown.length;
    return rest > 0 ? `${shown.join(', ')}${shown.length ? ' and ' : ''}${rest} more` : shown.join(', ');
}

// WHAT A MOVE DID, in words: how many of those asked moved, then the ones that
// didn't and why, by name — already gone, or not yours to move.
export function moveReport({ asked, moved, gone, refused, error }, titleOf) {
    const parts = [error
        ? `Moved ${moved.length} of ${asked} to the trash before it stopped (${error}).`
        : `Moved ${moved.length} of ${asked} to the trash.`];
    if (gone.length) parts.push(`${gone.length} ${gone.length === 1 ? 'was' : 'were'} already gone: ${named(gone, titleOf)}.`);
    if (refused.length) parts.push(`${refused.length} ${refused.length === 1 ? 'is' : 'are'} not yours to move: ${named(refused, titleOf)}.`);
    return parts.join(' ');
}

// HOW LONG THE TRASH KEEPS THEM, as the server is set: the trash list says
// whether its auto-purge runs (TRASH_AUTO_PURGE_ENABLED, off by default; when
// on, a node is deleted for good 30 days after it was trashed). Unknown (not
// yet read, or the read failed) promises no date.
export function keptWords(autoPurge) {
    if (autoPurge === true) return 'They stay restorable in the trash for 30 days, then are deleted for good.';
    if (autoPurge === false) return 'They stay restorable in the trash until they are deleted for good there.';
    return 'They stay restorable in the trash.';
}

function BulkDelete() {
    const { groupId, group } = usePlace();
    const toast = useToast();
    // IN A GROUP: deleting all is its admins' alone (the server's rule); deleting
    // some takes the delete permission an admin gives through a realm (bulk
    // delete has no author's exception). A member without it sees why, before
    // pressing anything.
    const access = useSpaceAccess();
    const groupName = group?.name || 'this group';
    const admin = access.isAdmin;
    const noDelete = access.can('delete') ? undefined : access.hint;
    const [state, setState] = useState(null); // { nodes, matching, total }
    const [facets, setFacets] = useState({ apps: {}, schemas: {} });
    const [schemas, setSchemas] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [text, setText] = useState('');
    const [search, setSearch] = useState('');
    const [bySchema, setBySchema] = useState([]);
    const [bySource, setBySource] = useState([]);
    const [autoPurge, setAutoPurge] = useState(null);
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null); // { text, tone, moved }
    const kept = keptWords(autoPurge);

    useEffect(() => {
        let live = true;
        api.getTrashedNodes({ limit: 1, offset: 0, ...(groupId ? { group_id: groupId } : {}) })
            .then((r) => { if (live && typeof r?.auto_purge_enabled === 'boolean') setAutoPurge(r.auto_purge_enabled); })
            .catch(() => {});
        return () => { live = false; };
    }, [groupId]);

    useEffect(() => {
        const t = setTimeout(() => setSearch(text.trim()), 300);
        return () => clearTimeout(t);
    }, [text]);
    const filtering = !!search || bySchema.length > 0 || bySource.length > 0;

    const params = useCallback((extra = {}) => {
        const p = { ...extra };
        if (search) p.search = search;
        if (bySchema.length) p.schema_ids = bySchema.join(',');
        if (bySource.length) p.app_names = bySource.join(',');
        return p;
    }, [search, bySchema, bySource]);

    // api.request, not api.getNodes: the latter drops `total`.
    const listNodes = useCallback((p) => {
        const q = new URLSearchParams({ skip_total: 'false', ...p }).toString();
        return api.request(groupId ? `/groups/${groupId}/nodes?${q}` : `/nodes?${q}`);
    }, [groupId]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [listed, all, facet, sch] = await Promise.all([
                listNodes(params({ limit: LOAD_LIMIT, offset: 0, fields: LIST_FIELDS })),
                listNodes({ limit: 1, fields: 'id' }),
                api.request(groupId ? `/groups/${groupId}/nodes/facets` : '/nodes/facets').catch(() => null),
                api.request(`/schemas?group_by_uri=true&limit=1000${groupId ? `&group_id=${encodeURIComponent(groupId)}` : ''}`).catch(() => ({ schemas: [] })),
            ]);
            const nodes = listed.nodes || [];
            setState({ nodes, matching: listed.total ?? nodes.length, total: all.total ?? (all.nodes || []).length });
            setSchemas(sch.schemas || []);
            // group facets: schemas as a list; personal: a map. Apps are a map in both,
            // counted over the whole graph, so options never vanish as you filter.
            if (facet && Array.isArray(facet.schemas)) {
                setFacets({ apps: facet.apps || {}, schemas: Object.fromEntries(facet.schemas.map((r) => [String(r.schema_id), r.count])) });
            } else {
                setFacets({ apps: facet?.apps || {}, schemas: facet?.schemas || {} });
            }
        } catch (e) {
            toast.error(e?.message || (groupId ? 'Could not read the group’s nodes' : 'Could not read your nodes'));
            setState((s) => s || { nodes: [], matching: 0, total: 0 });
        } finally {
            setLoading(false);
        }
    }, [groupId, listNodes, params, toast]);
    useEffect(() => { load(); }, [load]);

    const nodes = useMemo(() => state?.nodes || [], [state]);
    const sourceOf = (n) => (!n.created_by_third_party_id ? SELF_SOURCE
        : !n.created_by_app_name ? 'Unknown app'
            : n.created_by_kind === 'token' ? `Token: ${n.created_by_app_name}` : n.created_by_app_name);
    const sourceOptions = useMemo(() => {
        const counts = new Map(Object.entries(facets.apps || {}).map(([name, c]) => [name === 'User created' ? SELF_SOURCE : name, c]));
        // no facets (the read failed): the listed rows, leaving out an app with no
        // name, which the server's filter (by name) could never match
        if (counts.size === 0) {
            for (const n of nodes) if (!n.created_by_third_party_id || n.created_by_app_name) counts.set(sourceOf(n), (counts.get(sourceOf(n)) || 0) + 1);
        }
        return [...counts.entries()].map(([value, count]) => ({ value, count, label: value === SELF_SOURCE ? (groupId ? 'No app' : 'You (no app)') : value }))
            .sort((a, b) => b.count - a.count);
    }, [facets, nodes, groupId]);
    const schemaOptions = useMemo(() => schemas.map((s) => {
        let count = 0;
        for (const id of expandSchemaIds([s.id], schemas)) count += facets.schemas?.[String(id)] || 0;
        return { value: s.id, label: schemaLabel(s.schema_name), count };
    }).filter((o) => o.count > 0).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)), [schemas, facets]);
    const schemaName = (id) => schemaLabel(schemas.find((s) => s.id === id)?.schema_name) || null;

    const visibleSelected = nodes.filter((n) => selected.has(n.id)).map((n) => n.id);
    const hiddenSelected = selected.size - visibleSelected.length;
    const toggle = (id) => setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    const toggleAll = (on) => setSelected(on ? new Set(nodes.map((n) => n.id)) : new Set());

    const deleteIds = (ids) => (groupId ? api.bulkDeleteGroupNodes(groupId, ids) : api.bulkDeleteNodes(ids));
    // Chunk by chunk, keeping what each answered: the ids already gone, the ones
    // refused (no permission, or an app's declared node), and so the ones moved.
    // A chunk that throws stops the run; the earlier chunks are in the trash.
    const deleteInChunks = async (ids) => {
        const out = { asked: ids.length, moved: [], gone: [], refused: [], error: null };
        for (let i = 0; i < ids.length; i += DELETE_CHUNK) {
            const chunk = ids.slice(i, i + DELETE_CHUNK);
            let res;
            try { res = await deleteIds(chunk); } catch (e) { out.error = e?.message || 'the server refused'; break; }
            const gone = new Set((res?.not_found_ids || []).map(String));
            const refused = new Set([...(res?.denied_ids || []), ...(res?.sovereignty_blocked || [])].map(String));
            for (const id of chunk) {
                if (gone.has(String(id))) out.gone.push(id);
                else if (refused.has(String(id))) out.refused.push(id);
                else out.moved.push(id);
            }
        }
        return out;
    };
    const matchingIds = async () => {
        const ids = [];
        for (let offset = 0; ; offset += ID_PAGE) {
            const pageRes = await listNodes(params({ limit: ID_PAGE, offset, fields: 'id' }));
            const batch = pageRes.nodes || [];
            ids.push(...batch.map((n) => n.id));
            if (batch.length < ID_PAGE) break;
        }
        return ids;
    };
    const titles = useMemo(() => new Map(nodes.map((n) => [String(n.id), n.title || 'Untitled'])), [nodes]);
    const titleOf = (id) => titles.get(String(id));
    // Move, then say what happened and offer Undo; whatever happened, read the
    // list again — a run that stopped partway still moved its earlier chunks.
    const move = async (ids) => {
        try {
            const out = await deleteInChunks(ids);
            const text = moveReport(out, titleOf);
            setReport({ text, tone: out.error || out.gone.length || out.refused.length ? 'warn' : undefined, moved: out.moved });
            if (out.error) toast.error(text);
            else toast.success(`${plural(out.moved.length, 'node')} moved to the trash`);
            setSelected(new Set());
        } finally {
            await load();
        }
    };
    // Undo restores exactly the ids that moved, through the trash's own restore.
    const restore = (id) => (groupId ? api.restoreGroupNode(groupId, id) : api.restoreNode(id));
    const undo = async () => {
        const ids = report?.moved || [];
        let back = 0;
        setReport({ text: `Restoring ${plural(ids.length, 'node')}…`, moved: [] });
        try {
            for (let i = 0; i < ids.length; i += RESTORE_AT_ONCE) {
                const done = await Promise.allSettled(ids.slice(i, i + RESTORE_AT_ONCE).map(restore));
                back += done.filter((r) => r.status === 'fulfilled').length;
            }
        } finally {
            const missed = ids.length - back;
            setReport({ text: missed ? `Restored ${back} of ${ids.length}. The other ${missed} are still in Graph › Trash.` : `Restored ${plural(back, 'node')}.`, tone: missed ? 'warn' : undefined, moved: [] });
            await load();
        }
    };
    const finish = async (deleted) => {
        toast.success(`${plural(deleted, 'node')} moved to the trash`);
        setReport(null);
        setSelected(new Set());
        await load();
    };

    const matchTyped = typedConfirm('DELETE', `Every node matching the filter moves to the trash, ${state?.matching ?? 0} of them, including any not listed here. ${kept}`);
    const unlisted = state && state.total > nodes.length ? `, including ${state.total - nodes.length} not listed here` : '';
    // a group's graph is everyone's: its name, typed, is the confirmation
    const allTyped = groupId
        ? typedConfirm(groupName, `All ${state?.total ?? 0} of ${groupName}’s nodes move to the trash, for everyone in it${unlisted}. ${kept}`)
        : typedConfirm('DELETE ALL', `All ${state?.total ?? 0} of your nodes move to the trash${unlisted}. ${kept}`);
    const deleteAll = () => (groupId
        ? api.request(`/groups/${groupId}/nodes/delete-all`, { method: 'POST', body: { confirm: true } })
        : api.deleteAllNodes());
    // While the list and its counts are read again (or the search waits out its
    // pause), the counts on these buttons and in their asks are the old ones.
    const counting = loading || text.trim() !== search;
    const acts = [
        filtering && {
            id: 'matching', label: `Move matching ${state?.matching ?? 0} to trash`, danger: true,
            disabled: counting || !state?.matching || !!noDelete, hint: noDelete || (counting ? 'Counting…' : undefined),
            confirm: { title: `Move ${plural(state?.matching ?? 0, 'matching node')} to the trash?`, body: matchTyped.body, label: 'Move to trash' },
            run: async () => { matchTyped.check(); await move(await matchingIds()); },
        },
        !filtering && {
            id: 'all', label: `Move all ${state?.total ?? 0} to trash`, danger: true,
            disabled: counting || !admin || !state?.total,
            hint: access.onlyAdmin('delete all of its nodes') || (counting ? 'Counting…' : undefined),
            confirm: { title: groupId ? `Move all of ${groupName}’s nodes to the trash?` : `Move all ${plural(state?.total ?? 0, 'node')} to the trash?`, body: allTyped.body, label: 'Move to trash' },
            run: async () => { allTyped.check(); const res = await deleteAll(); await finish(res?.deleted_count ?? 0); },
        },
        {
            id: 'selected', label: visibleSelected.length ? `Move ${visibleSelected.length} selected to trash` : 'Move selected to trash', danger: true,
            disabled: visibleSelected.length === 0 || !!noDelete,
            hint: noDelete,
            confirm: { title: `Move ${plural(visibleSelected.length, 'node')} to the trash?`, body: kept, label: 'Move to trash' },
            run: async () => move(visibleSelected),
        },
    ].filter(Boolean);

    // who wrote it: you (no name in a group, where it is everyone's), or the app
    const writerOf = (n) => { const src = sourceOf(n); return src === SELF_SOURCE ? (groupId ? null : 'You') : src; };
    const pickColumns = [
        { id: 'item', label: 'Item', lock: true, min: 180, grow: 2, value: (n) => n.title || 'Untitled', cell: (n) => n.title || 'Untitled' },
        { id: 'kind', label: 'Kind', min: 100, grow: 1, keep: 2, value: (n) => (n.schema_id && schemaName(n.schema_id)) || null, cell: (n) => (n.schema_id && schemaName(n.schema_id)) || '—' },
        { id: 'writer', label: 'Written by', min: 100, grow: 1, keep: 3, value: writerOf, cell: (n) => writerOf(n) || '—' },
        { id: 'created', label: 'Created', min: 96, grow: 0, keep: 1, asc: false, value: (n) => (n.created_at ? new Date(n.created_at).getTime() : null), cell: (n) => dateOf(n.created_at) || '—' },
    ];

    return (
        <FormBand
            title="Move to trash in bulk"
            aside={state ? (filtering ? `${state.matching} match` : plural(state.total, 'node')) : undefined}
            lead={`Narrow by words, kind and the app that wrote them, then move what you selected or everything that matches to the trash. ${kept}`}
            acts={acts}
        >
            {noDelete && <Note>{noDelete}.</Note>}
            {report && (
                <Note tone={report.tone}>
                    {report.text}
                    {report.moved.length > 0 && <> <button type="button" className="nx-link" onClick={undo}>Undo</button></>}
                </Note>
            )}
            <div className="self-filters">
                <TextField id="self-bulk-search" label="Search" type="search" placeholder="Words in a title or value" value={text} onChange={(e) => setText(e.target.value)} />
                <TokenFilter label="Kinds" placeholder="Any kind" options={schemaOptions} selected={bySchema} onChange={setBySchema} />
                <TokenFilter label="Written by" placeholder="Anyone" options={sourceOptions} selected={bySource} onChange={setBySource} />
            </div>
            {state && (
                <>
                    <div className="self-picklist__bar">
                        <span className="self-picklist__count">
                            {visibleSelected.length} of {nodes.length} selected
                            {(filtering ? state.matching : state.total) > nodes.length && ` · showing the newest ${nodes.length}`}
                        </span>
                        {filtering && <button type="button" className="nx-link" onClick={() => { setText(''); setSearch(''); setBySchema([]); setBySource([]); }}>Clear filters</button>}
                    </div>
                    {hiddenSelected > 0 && <Note>{plural(hiddenSelected, 'more node')} selected but no longer listed, and won’t be moved.</Note>}
                    <div className="self-picklist">
                        <DataTable
                            id="self-data-nodes" label="Nodes" columns={pickColumns} rows={nodes} keyOf={(n) => n.id}
                            sort={{ id: 'created', dir: 'desc' }} fit sticky
                            picked={selected} onPick={(id) => toggle(id)} onPickAll={toggleAll} pickHeld={!!noDelete}
                            empty={filtering ? 'Nothing matches.' : 'No nodes yet.'}
                        />
                    </div>
                </>
            )}
        </FormBand>
    );
}

// A token filter: type to narrow, Enter or a press to add, × to remove,
// Backspace on an empty field pops the last. The server unions within one.
function TokenFilter({ label, options, selected, onChange, placeholder }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const box = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);
    const matches = options.filter((o) => !selected.includes(o.value))
        .filter((o) => !query || o.label.toLowerCase().includes(query.toLowerCase())).slice(0, 8);
    const add = (v) => { onChange([...selected, v]); setQuery(''); setCursor(0); setOpen(false); };
    const remove = (v) => onChange(selected.filter((x) => x !== v));
    const labelOf = (v) => options.find((o) => o.value === v)?.label || v;
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, matches.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        else if (e.key === 'Enter' && open && matches[cursor]) { e.preventDefault(); add(matches[cursor].value); }
        else if (e.key === 'Escape') setOpen(false);
        else if (e.key === 'Backspace' && !query && selected.length) remove(selected[selected.length - 1]);
    };
    return (
        <div ref={box} className="self-token">
            <span className="self-select__label">{label}</span>
            <div className="self-token__field" onClick={() => { box.current?.querySelector('input')?.focus(); setOpen(true); }}>
                {selected.map((v) => (
                    <span key={v} className="self-token__chip">
                        {labelOf(v)}
                        <button type="button" aria-label={`Remove ${labelOf(v)}`} onClick={(e) => { e.stopPropagation(); remove(v); }}>×</button>
                    </span>
                ))}
                <input value={query} aria-label={label} placeholder={selected.length ? '' : placeholder}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
                    onFocus={() => setOpen(true)} onKeyDown={onKeyDown} />
            </div>
            {open && matches.length > 0 && (
                <div className="self-token__menu" role="listbox">
                    {matches.map((o, i) => (
                        <div key={o.value} role="option" aria-selected={i === cursor} className={`self-token__opt${i === cursor ? ' is-on' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); add(o.value); }} onMouseEnter={() => setCursor(i)}>
                            <span>{o.label}</span><span className="self-token__n">{o.count}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

